// src/App.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.scss";
import {
    ChatPanel,
    type SessionControl,
} from "./components/ChatPanel/ChatPanel";
import DigitalHumanChat, {
    DigitalHumanChatRef,
} from "./components/DigitalHuman/DigitalHumanChat";
import { StatusBar } from "./components/StatusBar/StatusBar";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { useStreamingSocket } from "./hooks/useStreamingSocket";
import type { ChatMessage } from "./types";
import {
    AVATAR_HTTP_CONFIG,
    TTS_TAIL_CONFIG,
    WS_CONFIG,
} from "./utils/constants";
import {
    formatAvatarSessionError,
    isAvatarBusyError,
} from "./utils/avatarSessionError";
import { stripMarkdown } from "./utils/markdown";
import {
    concatArrayBuffers,
    decodeBase64ToArrayBuffer,
    toEvenArrayBuffer,
} from "./utils/pcm";

type InquiryPhase = "landing" | "inquiry";

function locatePiece(
    text: string,
    piece: string,
    from: number,
): { start: number; end: number } | null {
    const needle = piece.replace(/\s+/g, "");
    if (!needle) return null;
    for (let ti = Math.max(0, from); ti < text.length; ti++) {
        let pi = 0;
        let start = -1;
        let i = ti;
        while (i < text.length && pi < needle.length) {
            const ch = text[i] ?? "";
            if (/\s/.test(ch)) {
                i += 1;
                continue;
            }
            if (ch !== needle[pi]) break;
            if (start < 0) start = i;
            pi += 1;
            i += 1;
        }
        if (pi === needle.length && start >= 0) return { start, end: i };
    }
    return null;
}

const App: React.FC = () => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isSessionStarting, setIsSessionStarting] = useState(false);
    const [phase, setPhase] = useState<InquiryPhase>("landing");
    const [avatarReady, setAvatarReady] = useState(false);
    const [avatarSpeaking, setAvatarSpeaking] = useState(false);
    const [speechRange, setSpeechRange] = useState<{ start: number; end: number } | null>(null);
    const [replyText, setReplyText] = useState("");
    const [broadcastPaused, setBroadcastPaused] = useState(false);
    const [audible, setAudible] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const [browserAudioFallback, setBrowserAudioFallback] = useState(false);
    const [sessionControl, setSessionControl] = useState<SessionControl>(null);

    const sessionNonceRef = useRef(0);
    const sessionStartedRef = useRef(false);
    const leavingRef = useRef(false);
    const avatarRetryRef = useRef(0);
    const avatarReadyRef = useRef(false);
    const ttsFinishTimerRef = useRef<number | null>(null);
    const ttsTurnBytesRef = useRef(0);
    const ttsTurnStartRef = useRef(0);
    const lastChunkAtRef = useRef(0);
    const lastChunkBytesRef = useRef(0);
    const ttsSampleRateRef = useRef(TTS_TAIL_CONFIG.SAMPLE_RATE);
    const pendingTtsRef = useRef<ArrayBuffer[]>([]);
    const pendingTtsFinishRef = useRef(false);
    const digitalHumanRef = useRef<DigitalHumanChatRef>(null);
    const browserAudioFallbackRef = useRef(false);
    const browserChunksRef = useRef<ArrayBuffer[]>([]);
    const ttsStreamDoneRef = useRef(false);
    const speechSettledRef = useRef(false);
    const speechTailAtRef = useRef(0);
    const speechPlacedRef = useRef<
        Array<{ text: string; startSec: number; start: number; end: number }>
    >([]);
    const replyTextRef = useRef("");
    replyTextRef.current = replyText;

    const { play: playBrowserTts, stop: stopBrowserTts } = useAudioPlayer({
        onEnded: () => setAvatarSpeaking(false),
        onError: () => setAvatarSpeaking(false),
    });

    const {
        isConnected,
        sendMessage,
        subscribe,
        reconnect,
        reconnectAttempts,
        reconnectExhausted,
        maxReconnectAttempts,
    } = useStreamingSocket(WS_CONFIG.URL);

    const clearTtsFinishTimer = () => {
        if (ttsFinishTimerRef.current) {
            clearTimeout(ttsFinishTimerRef.current);
            ttsFinishTimerRef.current = null;
        }
    };

    /** 播报收口兜底：仅作安全网；正常靠 onPlaybackEnd。延迟偏长，避免过早开麦吃到数字人尾音 */
    const scheduleAvatarSpeakingRelease = useCallback(() => {
        clearTtsFinishTimer();
        const sampleRate = Math.max(1, ttsSampleRateRef.current);
        const pcmMs = (ttsTurnBytesRef.current / 2 / sampleRate) * 1000;
        const elapsed =
            ttsTurnStartRef.current > 0
                ? Date.now() - ttsTurnStartRef.current
                : 0;
        const remainMs = Math.max(0, pcmMs - elapsed);
        const delay =
            remainMs +
            TTS_TAIL_CONFIG.SILENCE_MS +
            TTS_TAIL_CONFIG.FINISH_EXTRA_MS +
            TTS_TAIL_CONFIG.RESUME_GUARD_MS +
            1500;
        ttsFinishTimerRef.current = window.setTimeout(() => {
            ttsFinishTimerRef.current = null;
            setAvatarSpeaking(false);
        }, Math.max(delay, 4000));
    }, []);

    const leaveInquiry = useCallback(() => {
        if (leavingRef.current && phase === "landing") return;
        leavingRef.current = true;
        clearTtsFinishTimer();
        if (sessionId) {
            sendMessage({
                type: "end_session",
                sessionId,
                timestamp: Date.now(),
            });
        }
        sessionNonceRef.current += 1;
        setSessionControl({ action: "end", nonce: sessionNonceRef.current });
        digitalHumanRef.current?.exit();
        sessionStartedRef.current = false;
        avatarRetryRef.current = 0;
        avatarReadyRef.current = false;
        browserAudioFallbackRef.current = false;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        browserChunksRef.current = [];
        ttsTurnBytesRef.current = 0;
        ttsTurnStartRef.current = 0;
        stopBrowserTts();
        setAvatarReady(false);
        setAvatarSpeaking(false);
        setBroadcastPaused(false);
        speechPlacedRef.current = [];
        setSpeechRange(null);
        setReplyText("");
        setAudible(false);
        setAvatarError(null);
        setBrowserAudioFallback(false);
        setIsSessionActive(false);
        setSessionId(null);
        setMessages([]);
        setPhase("landing");
        window.setTimeout(() => {
            leavingRef.current = false;
        }, 0);
    }, [phase, sessionId, sendMessage, stopBrowserTts]);

    const enterInquiry = useCallback(() => {
        if (!isConnected || phase === "inquiry") return;
        leavingRef.current = false;
        sessionStartedRef.current = false;
        avatarRetryRef.current = 0;
        setAvatarError(null);
        setAvatarReady(false);
        setAvatarSpeaking(false);
        setBrowserAudioFallback(false);
        avatarReadyRef.current = false;
        browserAudioFallbackRef.current = false;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        browserChunksRef.current = [];
        stopBrowserTts();
        // 用户手势里预热扬声器，避免并发降级后欢迎语被浏览器拦截
        const warm = new Audio();
        warm.src =
            "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";
        void warm.play().catch(() => undefined);
        setPhase("inquiry");
    }, [isConnected, phase, stopBrowserTts]);

    const handleManualReconnect = useCallback(() => {
        reconnect();
        if (phase === "inquiry") {
            setAvatarError(null);
            setAvatarReady(false);
            setAvatarSpeaking(false);
            setBrowserAudioFallback(false);
            avatarReadyRef.current = false;
            browserAudioFallbackRef.current = false;
            pendingTtsRef.current = [];
            pendingTtsFinishRef.current = false;
            browserChunksRef.current = [];
            stopBrowserTts();
            sessionStartedRef.current = false;
            digitalHumanRef.current?.exit();
            window.setTimeout(() => {
                digitalHumanRef.current?.start();
            }, 200);
        }
    }, [phase, reconnect, stopBrowserTts]);

    const isAvatarPlayable = useCallback(() => {
        if (browserAudioFallbackRef.current) return false;
        const avatar = digitalHumanRef.current;
        return (
            avatarReadyRef.current &&
            !!avatar &&
            (typeof avatar.isReady !== "function" || avatar.isReady())
        );
    }, []);

    const playBrowserCollected = useCallback(() => {
        const chunks = browserChunksRef.current;
        browserChunksRef.current = [];
        if (chunks.length === 0) {
            setAvatarSpeaking(false);
            return;
        }
        const merged = concatArrayBuffers(chunks);
        void playBrowserTts(new Uint8Array(merged));
    }, [playBrowserTts]);

    const flushPendingTts = useCallback(() => {
        if (browserAudioFallbackRef.current) {
            browserChunksRef.current = [
                ...browserChunksRef.current,
                ...pendingTtsRef.current,
            ];
            pendingTtsRef.current = [];
            if (pendingTtsFinishRef.current) {
                pendingTtsFinishRef.current = false;
                setAvatarSpeaking(true);
                playBrowserCollected();
            }
            return;
        }
        if (!isAvatarPlayable()) return;
        const avatar = digitalHumanRef.current;
        if (!avatar) return;
        const queued = pendingTtsRef.current;
        const shouldFinish = pendingTtsFinishRef.current;
        if (queued.length === 0 && !shouldFinish) return;
        pendingTtsRef.current = [];
        pendingTtsFinishRef.current = false;
        setAvatarSpeaking(true);
        ttsTurnStartRef.current = Date.now();
        for (const buffer of queued) {
            lastChunkBytesRef.current = buffer.byteLength;
            avatar.playTTSAudio(buffer);
        }
        if (shouldFinish) {
            avatar.finishTTSAudio();
            scheduleAvatarSpeakingRelease();
        }
    }, [isAvatarPlayable, playBrowserCollected, scheduleAvatarSpeakingRelease]);

    const handleTTSStart = useCallback((sampleRate?: number) => {
        if (typeof sampleRate === "number" && sampleRate > 0) {
            ttsSampleRateRef.current = sampleRate;
        } else {
            ttsSampleRateRef.current = TTS_TAIL_CONFIG.SAMPLE_RATE;
        }
        clearTtsFinishTimer();
        pendingTtsFinishRef.current = false;
        pendingTtsRef.current = [];
        ttsTurnBytesRef.current = 0;
        ttsTurnStartRef.current = 0;
        lastChunkAtRef.current = 0;
        lastChunkBytesRef.current = 0;
        setAvatarSpeaking(true);
        setBroadcastPaused(false);
        ttsStreamDoneRef.current = false;
        speechSettledRef.current = false;
        speechTailAtRef.current = 0;
        speechPlacedRef.current = [];
        setSpeechRange(null);
        if (browserAudioFallbackRef.current) {
            stopBrowserTts();
            browserChunksRef.current = [];
            return;
        }
        digitalHumanRef.current?.beginTTSTurn();
    }, [stopBrowserTts]);

    const handleTTSPiece = useCallback((text: string) => {
        digitalHumanRef.current?.setSpeechPiece(text);
    }, []);

    useEffect(() => {
        if (!avatarSpeaking) {
            speechTailAtRef.current = 0;
            speechPlacedRef.current = [];
            setSpeechRange(null);
            return;
        }
        // 前 15 秒与出声对齐。之后云端略快，按大约每秒补 0.11 秒，把后半段两三秒的落后追回来。
        const CATCHUP_AFTER = 15;
        const CATCHUP_RATE = 0.11;
        const CLEAR_HOLD_MS = 280;
        const tick = () => {
            if (speechSettledRef.current) return;
            const playback = digitalHumanRef.current?.getPlayback() ?? {
                playedSec: 0,
                queuedSec: 0,
                active: false,
            };
            let { playedSec, queuedSec, active } = playback;
            if (
                !active &&
                browserAudioFallbackRef.current &&
                ttsTurnBytesRef.current > 0 &&
                ttsTurnStartRef.current > 0
            ) {
                const sampleRate = Math.max(1, ttsSampleRateRef.current);
                queuedSec = ttsTurnBytesRef.current / 2 / sampleRate;
                playedSec = Math.max(
                    0,
                    Math.min(
                        queuedSec,
                        (Date.now() - ttsTurnStartRef.current) / 1000,
                    ),
                );
                active = queuedSec > 0;
            }
            const timelineDone =
                ttsStreamDoneRef.current &&
                queuedSec > 0.05 &&
                playedSec >= queuedSec - 0.08;
            if (timelineDone && !speechTailAtRef.current) {
                speechTailAtRef.current = Date.now();
            }
            if (
                timelineDone &&
                speechTailAtRef.current > 0 &&
                Date.now() - speechTailAtRef.current >= CLEAR_HOLD_MS
            ) {
                speechSettledRef.current = true;
                setSpeechRange(null);
                return;
            }
            const text = stripMarkdown(replyTextRef.current);
            const marks = digitalHumanRef.current?.getSpeechMarks() ?? [];
            const placed = speechPlacedRef.current;
            while (placed.length < marks.length) {
                const mark = marks[placed.length];
                if (!mark) break;
                const from = placed.length ? placed[placed.length - 1]!.end : 0;
                const range = locatePiece(text, mark.text, from);
                if (!range) break;
                placed.push({
                    text: mark.text,
                    startSec: mark.startSec,
                    start: range.start,
                    end: range.end,
                });
            }
            const extra = Math.max(0, playedSec - CATCHUP_AFTER) * CATCHUP_RATE;
            const heard = Math.min(queuedSec, playedSec + extra);
            let current: { start: number; end: number } | null = null;
            for (let i = 0; i < placed.length; i++) {
                const piece = placed[i]!;
                if (heard < piece.startSec) break;
                current = { start: piece.start, end: piece.end };
            }
            if (!current) return;
            setSpeechRange((prev) =>
                prev && prev.start === current.start && prev.end === current.end
                    ? prev
                    : current,
            );
        };
        tick();
        const timer = window.setInterval(tick, 40);
        return () => window.clearInterval(timer);
    }, [avatarSpeaking]);

    const handleAudioChunk = useCallback(
        (audioBase64: string | null, isLast: boolean) => {
            const useBrowser = browserAudioFallbackRef.current;
            const playable = !useBrowser && isAvatarPlayable();

            if (audioBase64) {
                try {
                    const buffer = toEvenArrayBuffer(
                        decodeBase64ToArrayBuffer(audioBase64),
                    );
                    lastChunkBytesRef.current = buffer.byteLength;
                    lastChunkAtRef.current = Date.now();
                    if (ttsTurnBytesRef.current === 0) {
                        ttsTurnStartRef.current = Date.now();
                    }
                    ttsTurnBytesRef.current += buffer.byteLength;
                    clearTtsFinishTimer();
                    pendingTtsFinishRef.current = false;
                    if (useBrowser) {
                        browserChunksRef.current.push(buffer);
                    } else if (playable) {
                        digitalHumanRef.current?.playTTSAudio(buffer);
                    } else {
                        const queuedBytes = pendingTtsRef.current.reduce(
                            (sum, item) => sum + item.byteLength,
                            0,
                        );
                        if (
                            queuedBytes + buffer.byteLength <=
                            TTS_TAIL_CONFIG.PENDING_MAX_BYTES
                        ) {
                            pendingTtsRef.current.push(buffer);
                        } else {
                            console.warn(
                                "[TTS] 数字人未就绪，待播队列已满，丢弃后续分片",
                            );
                        }
                    }
                } catch (e) {
                    console.error("解码音频失败", e);
                }
            }

            if (isLast) {
                ttsStreamDoneRef.current = true;
                if (useBrowser) {
                    pendingTtsFinishRef.current = false;
                    clearTtsFinishTimer();
                    playBrowserCollected();
                } else if (playable) {
                    pendingTtsFinishRef.current = false;
                    digitalHumanRef.current?.finishTTSAudio();
                    scheduleAvatarSpeakingRelease();
                } else {
                    pendingTtsFinishRef.current = true;
                    // 数字人未就绪时仍要释放倾听锁，否则语音通道会永久卡在「正在播报」
                    scheduleAvatarSpeakingRelease();
                }
            }
        },
        [
            isAvatarPlayable,
            playBrowserCollected,
            scheduleAvatarSpeakingRelease,
        ],
    );

    const handleDigitalHumanReady = useCallback(() => {
        setAvatarError(null);
        avatarRetryRef.current = 0;
        avatarReadyRef.current = true;
        browserAudioFallbackRef.current = false;
        setBrowserAudioFallback(false);
        setAvatarReady(true);
        flushPendingTts();
    }, [flushPendingTts]);

    const handleAvatarPlaybackEnd = useCallback(() => {
        // 本地 PCM 送完不等于喇叭停了。云端还要播完缓冲，这段时间不能开麦。
        clearTtsFinishTimer();
        const sampleRate = Math.max(1, ttsSampleRateRef.current);
        const pcmMs = (ttsTurnBytesRef.current / 2 / sampleRate) * 1000;
        const elapsed =
            ttsTurnStartRef.current > 0
                ? Date.now() - ttsTurnStartRef.current
                : 0;
        const remainMs = Math.max(0, pcmMs - elapsed);
        const tail = remainMs + TTS_TAIL_CONFIG.SILENCE_MS + 1800;
        ttsFinishTimerRef.current = window.setTimeout(() => {
            ttsFinishTimerRef.current = null;
            setAvatarSpeaking(false);
            setBroadcastPaused(false);
        }, tail);
    }, []);

    const toggleBroadcast = useCallback(() => {
        if (broadcastPaused) {
            digitalHumanRef.current?.resumeBroadcast();
            setBroadcastPaused(false);
            setAvatarSpeaking(true);
            scheduleAvatarSpeakingRelease();
            return;
        }
        clearTtsFinishTimer();
        digitalHumanRef.current?.pauseBroadcast();
        setBroadcastPaused(true);
        // 手动停止后喇叭已打断，这时才允许倾听
        setAvatarSpeaking(false);
    }, [broadcastPaused, scheduleAvatarSpeakingRelease]);

    const handleDigitalHumanError = useCallback((error: {
        message?: string;
        terminate?: boolean;
        code?: string;
        busy?: boolean;
    }) => {
        const msg = formatAvatarSessionError(error?.message, error?.code);
        const busy =
            error?.busy || isAvatarBusyError(error?.message, error?.code);
        setAvatarError(msg);
        avatarReadyRef.current = false;
        setAvatarReady(false);
        if (busy) {
            browserAudioFallbackRef.current = true;
            setBrowserAudioFallback(true);
            if (pendingTtsRef.current.length > 0) {
                browserChunksRef.current.push(...pendingTtsRef.current);
                pendingTtsRef.current = [];
            }
            if (pendingTtsFinishRef.current) {
                pendingTtsFinishRef.current = false;
                playBrowserCollected();
            }
            return;
        }
        setAvatarSpeaking(false);
        if (phase !== "inquiry" || leavingRef.current) return;
        if (avatarRetryRef.current < 1) {
            avatarRetryRef.current += 1;
            window.setTimeout(() => {
                if (leavingRef.current) return;
                digitalHumanRef.current?.start();
            }, 2500);
        }
    }, [phase, playBrowserCollected]);

    const handleSessionChange = useCallback(
        (id: string | null) => {
            setSessionId(id);
            setIsSessionActive(!!id);
            if (
                !id &&
                isConnected &&
                phase === "inquiry" &&
                !leavingRef.current
            ) {
                leaveInquiry();
            }
        },
        [phase, isConnected, leaveInquiry],
    );

    const handleMessageUpdate = useCallback((newMessages: ChatMessage[]) => {
        setMessages(newMessages);
    }, []);

    useEffect(() => {
        if (phase === "inquiry" && !isConnected) {
            sessionStartedRef.current = false;
        }
    }, [phase, isConnected]);

    useEffect(() => {
        if (
            phase === "inquiry" &&
            isConnected &&
            !sessionStartedRef.current
        ) {
            sessionStartedRef.current = true;
            sessionNonceRef.current += 1;
            setSessionControl({
                action: "start",
                nonce: sessionNonceRef.current,
            });
        }
    }, [phase, isConnected]);

    useEffect(() => {
        return () => clearTtsFinishTimer();
    }, []);

    const inquiryPushedRef = useRef(false);
    useEffect(() => {
        if (phase !== "inquiry") {
            inquiryPushedRef.current = false;
            return;
        }
        if (!inquiryPushedRef.current) {
            window.history.pushState({ ugvInquiry: 1 }, "");
            inquiryPushedRef.current = true;
        }
        const onPop = () => {
            if (leavingRef.current) return;
            inquiryPushedRef.current = false;
            leaveInquiry();
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [phase, leaveInquiry]);

    return (
        <div className="app">
            <StatusBar
                isConnected={isConnected}
                isSessionActive={isSessionActive}
                sessionId={sessionId}
                messageCount={messages.length}
                reconnectAttempts={reconnectAttempts}
                maxReconnectAttempts={maxReconnectAttempts}
                reconnectExhausted={reconnectExhausted}
                phase={phase}
                avatarReady={avatarReady}
                avatarError={avatarError}
                onReconnect={handleManualReconnect}
                onEndInquiry={phase === "inquiry" ? leaveInquiry : undefined}
            />

            {phase === "landing" ? (
                <div className="landing">
                    <button
                        type="button"
                        className={`landing-cta ${!isConnected ? "disabled" : ""}`}
                        onClick={enterInquiry}
                        disabled={!isConnected}
                    >
                        <span className="landing-cta-rings" aria-hidden>
                            <span />
                            <span />
                            <span />
                        </span>
                        <span className="landing-cta-glow" />
                        <span className="landing-cta-label">
                            开启数字人AI问询
                        </span>
                    </button>
                    <p className="landing-hint">
                        {isConnected
                            ? "连接已就绪，点击进入问询"
                            : reconnectExhausted
                              ? "通信中断，请点击右上角「一键重连」"
                              : "正在建立通信连接…"}
                    </p>
                </div>
            ) : (
                <div className="main-layout">
                    <div className="digital-human-section">
                        <button
                            type="button"
                            className={`dh-speaker ${
                                broadcastPaused
                                    ? "is-paused"
                                    : audible
                                      ? "is-speaking"
                                      : "is-idle"
                            }`}
                            onClick={
                                avatarSpeaking || broadcastPaused || audible
                                    ? toggleBroadcast
                                    : undefined
                            }
                            disabled={!avatarSpeaking && !broadcastPaused && !audible}
                            aria-label={
                                broadcastPaused
                                    ? "继续播报"
                                    : audible
                                      ? "停止播报"
                                      : "未在播报"
                            }
                            title={
                                broadcastPaused
                                    ? "继续播报"
                                    : audible
                                      ? "停止播报"
                                      : "未在播报"
                            }
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 9.5h3.2L12 5.2v13.6l-4.8-4.3H4z" />
                                {broadcastPaused ? (
                                    <path d="M15.2 8.2v7.6l6-3.8z" />
                                ) : avatarSpeaking ? (
                                    <>
                                        <path
                                            className="dh-speaker-wave dh-speaker-wave--1"
                                            d="M15.2 9.2a3.2 3.2 0 0 1 0 5.6"
                                        />
                                        <path
                                            className="dh-speaker-wave dh-speaker-wave--2"
                                            d="M17.6 6.6a6.4 6.4 0 0 1 0 10.8"
                                        />
                                    </>
                                ) : (
                                    <path d="M16 9.2l5.6 5.6M21.6 9.2L16 14.8" />
                                )}
                            </svg>
                        </button>
                        <DigitalHumanChat
                            ref={digitalHumanRef}
                            baseUrl={AVATAR_HTTP_CONFIG.URL}
                            autoStart
                            showLauncher={false}
                            onReady={handleDigitalHumanReady}
                            onPlaybackEnd={handleAvatarPlaybackEnd}
                            onAudibleChange={setAudible}
                            onError={handleDigitalHumanError}
                            onMessage={(msg) =>
                                console.log("数字人消息:", msg.text)
                            }
                        />
                    </div>
                    <div className="chat-section">
                        <ChatPanel
                            isConnected={isConnected}
                            sendMessage={sendMessage}
                            subscribe={subscribe}
                            sessionControl={sessionControl}
                            onSessionChange={handleSessionChange}
                            onSessionStartingChange={setIsSessionStarting}
                            onMessageUpdate={handleMessageUpdate}
                            onAudioChunk={handleAudioChunk}
                            onTTSStart={handleTTSStart}
                            avatarReady={avatarReady}
                            avatarSpeaking={avatarSpeaking}
                            speechRange={speechRange}
                            onReplyText={setReplyText}
                            onTTSPiece={handleTTSPiece}
                            avatarError={avatarError}
                            browserAudioFallback={browserAudioFallback}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
